import { useCallback, useState } from "react";
import { format, subDays } from "date-fns";
import toast from "react-hot-toast";

import DataTable from "react-data-table-component";
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import TableLoader from "./TableLoader";
import ButtonLoader from "./ButtonLoader";
import UserProfilePhoto from "./UserProfilePhoto";

import allocationService from "../services/allocationService";

import useAllocationReadyLabourers from "../hooks/useAllocationReadyLabourers";

import { formatDateToSydneyTimeZone, toApiDateTime } from "../utils/dateUtils";

const ProjectAllocationForm = ({ project, onCloseClicked, onAllocate }) => {
  const [selectedRows, setSelectedRows] = useState([]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [dateRange, setDateRange] = useState([null, null]);
  const [isAllocating, setIsAllocating] = useState(false);

  const startDateIso = dateRange[0] ? toApiDateTime(format(dateRange[0], 'yyyy-MM-dd')) : null;
  const endDateIso = dateRange[1] ? toApiDateTime(format(dateRange[1], 'yyyy-MM-dd')) : null;

  const { data, isLoading } = useAllocationReadyLabourers({
    shouldFetch: !!startDateIso && !!endDateIso,
    projectId: project._id,
    startDate: startDateIso,
    endDate: endDateIso,
    page,
    perPage
  });

  const labourers = data?.labourers;
  const labourerCount = data?.count;

  const handleRowSelected = useCallback(state => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const labourerColumns = [
    {
      cell: row => <UserProfilePhoto photoUrl={row.user.profilePicture} fullName={row.user.fullName} />,
      width: '75px'
    },
    {
      name: 'Name',
      selector: row => row.user.fullName
    },
    {
      name: 'Email',
      selector: row => row.user.email
    },
    {
      name: 'Date of Birth',
      selector: row => formatDateToSydneyTimeZone(row.user?.dateOfBirth)
    }
  ];

  const handleAllocate = async () => {
    if (selectedRows.length === 0) return;
    if (!startDateIso || !endDateIso) return;

    const labourerIds = selectedRows.map(row => row._id);

    const uniqueLabourerIds = [...new Set(labourerIds)];

    setIsAllocating(true);

    try {
      const requests = uniqueLabourerIds.map(async labourerId => {
        return allocationService.allocateLabourer({
          labourerId: labourerId,
          projectId: project._id,
          startDate: startDateIso,
          endDate: endDateIso
        });
      });

      await Promise.all(requests);

      onAllocate();
      toast.success('Labourers allocated', { icon: '🚀' });
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while allocating labourers');
    } finally {
      setIsAllocating(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[900px] min-h-[450px] pb-1 overflow-visible">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>{project.name} Allocation</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-4 py-4">
        <div className="flex w-full md:w-auto items-center gap-3">
          <label className="flex items-center gap-2">
            <span className="text-xs font-semibold leading-0">Date range</span>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              clearIcon={null}
              format="d/M/yyyy"
              className="h-[36px]"
              minDate={new Date(project.startDate)}
              maxDate={new Date(project.endDate)}
              activeStartDate={new Date(project.startDate)}
              maxDetail="month"
              minDetail="year"
              required
            />
          </label>
        </div>

        <button
          onClick={handleAllocate}
          className="ase-button-primary flex items-center justify-center grow h-[36px]"
        >
          {isAllocating ? <ButtonLoader /> : 'Allocate'}
        </button>
      </div>

      {!startDateIso && !endDateIso && (
        <div className="flex flex-grow items-center justify-center py-4 px-4 md:py-8 text-center">
          <p className="text-base">Select a date range to view available labourers</p>
        </div>
      )}

      {startDateIso && endDateIso && (
        <DataTable
          columns={labourerColumns}
          data={labourers}
          striped
          highlightOnHover
          pagination
          paginationServer
          pointerOnHover
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          paginationTotalRows={labourerCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          selectableRows
          selectableRowsHighlight
          onSelectedRowsChange={handleRowSelected}
          noDataComponent={
            <div className="flex items-center justify-center py-8">
              No labourers available for allocation
            </div>
          }
        />
      )}
    </div>
  )
};

export default ProjectAllocationForm;
